import { useApolloClient } from "@apollo/client";
import { pick } from "lodash-es";
import React from "react";
import { useLatest } from "react-use";
import { ItemType } from "../../../../clients/graphqlTypes.js";
import { isAllowedToPaste } from "../../../features/doc-items/constraints.js";
import {
  CreateItemFromTemplateArgs,
  CreateItemsFromTemplatesArgs,
  CreateItemsWithProductsArgs,
  CreateItemWithLinkedTemplateArgs,
  useCreateItem,
} from "../../../features/doc-items/hooks/useCreateItem.js";
import { useDocItemsClipboard } from "../../../features/doc-items/hooks/useDocItemsClipboard.js";
import { CreateQuoteTemplateProcessRef } from "../../../features/templates/CreateQuoteTemplateModal.js";
import { QUOTE_TEMPLATE_ITEM_TYPES } from "../../../constants.js";
import {
  useTemplateQuoteEdit_CreateItemsMutation,
  useTemplateQuoteEdit_CreateTemplateMutation,
  useTemplateQuoteEdit_PasteItemFromClipboardMutation,
  useTemplateQuoteEdit_SetItemProductMutation,
} from "../../../main-routes/templates/quote/TemplateQuoteEdit.generated.js";

/**
 * Create / paste item operations for a quote template, shared by the template
 * tree editor (`TemplateQuoteEdit`) and the template data grid
 * (`TemplateQuoteDataGrid`).
 */
export function useCreateTemplateItems({
  templateId,
  createQuoteTemplateRef,
  expandedItemIds,
}: {
  templateId: string;
  // Only the tree editor wires this (to a CreateQuoteTemplateModal) for the
  // create-with-linked-template flow; the grid omits it.
  createQuoteTemplateRef?: React.RefObject<CreateQuoteTemplateProcessRef>;
  expandedItemIds: string[] | undefined;
}) {
  const client = useApolloClient();
  const expandedItemIdsRef = useLatest(expandedItemIds);
  const [createItems] = useTemplateQuoteEdit_CreateItemsMutation({
    client,
  });

  const { createItem, createItemSection, createItemByType } = useCreateItem({
    projectId: null,
    createDocumentItems: createItems,
    expandedItemIds,
  });

  const createItemFromTemplate = React.useCallback(
    async ({
      title,
      parentItemId,
      templateQuoteId,
      newItemRank,
    }: CreateItemFromTemplateArgs) => {
      const result = await createItemSection(
        title,
        templateId,
        parentItemId,
        newItemRank,
        templateQuoteId
      );

      return result;
    },
    [createItemSection, templateId]
  );

  const createItemsFromTemplates = React.useCallback(
    async ({
      parentItemId,
      templates,
      newItemRank,
    }: CreateItemsFromTemplatesArgs) => {
      const result = await createItems({
        variables: {
          input: {
            projectId: null,
            docId: templateId,
            parentItemId,
            items: templates.map(opts => ({
              title: opts.template.title,
              type: "section",
              decisionBehaviorOfSubitems: "NONE",
              estimatedQuantity: opts.quantity,
              templateQuoteId: opts.template.id,
            })),
            expandedItemIds: expandedItemIdsRef.current,
            rank: newItemRank,
          },
        },
      });

      return result.data!.createItems.items;
    },
    [createItems, expandedItemIdsRef, templateId]
  );

  const createItemsWithProducts = React.useCallback(
    async ({
      parentItemId,
      products,
      newItemRank,
    }: CreateItemsWithProductsArgs) => {
      const result = await createItems({
        variables: {
          input: {
            projectId: null,
            docId: templateId,
            parentItemId,
            items: products.map(opts =>
              "__typename" in opts.product
                ? {
                    title: opts.product.texts?.title ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    product: pick(opts.product, "supplierId", "articleNumber"),
                  }
                : {
                    title: opts.product.productTitle ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    productValues: opts.product,
                  }
            ),
            expandedItemIds: expandedItemIdsRef.current,
            rank: newItemRank,
          },
        },
      });

      return result.data!.createItems.items;
    },
    [createItems, expandedItemIdsRef, templateId]
  );

  const [createQuoteTemplate] = useTemplateQuoteEdit_CreateTemplateMutation({
    client,
  });
  const [setItemProduct] = useTemplateQuoteEdit_SetItemProductMutation({
    client,
  });

  // TODO: ask for template when creating from tree input
  // const askRootItemType = useAskRootItemType();
  const createItemWithLinkedTemplate = React.useCallback(
    async ({
      title,
      parentItemId,
      newItemRank,
    }: CreateItemWithLinkedTemplateArgs) => {
      const values =
        (await createQuoteTemplateRef?.current?.createQuoteTemplate(title)) ??
        null;
      if (!values) return null;

      const quoteTemplateResult = await createQuoteTemplate({
        variables: {
          input: {
            title: values.title,
            description: values.description,
            discountPercentage: 0,
            rootItemType: "section",
            applyTemplate: true,
            templateTypeId: values.templateType?.id,
            category: values.category,
          },
        },
      });

      const newTemplateId =
        quoteTemplateResult.data?.createQuoteTemplate.quoteTemplate.id;

      if (!newTemplateId) throw new Error("Document id missing");

      const rootItemId =
        quoteTemplateResult.data?.createQuoteTemplate.rootItem.id;
      const rootItemType =
        quoteTemplateResult.data?.createQuoteTemplate.rootItem.type;

      if (!rootItemId || !rootItemType) throw new Error("Root item id missing");

      if (rootItemType === "paid" && values.product) {
        await setItemProduct({
          variables: {
            input: {
              itemId: rootItemId,
              docId: newTemplateId,
              projectId: null,
              productArticleNumber: values.product.articleNumber,
              productSupplierId: values.product.supplierId,
              expandedItemIds: [parentItemId],
            },
          },
        });
      }

      await createItemSection(
        title,
        templateId,
        parentItemId,
        newItemRank,
        newTemplateId
      );
    },
    [
      createQuoteTemplateRef,
      createQuoteTemplate,
      createItemSection,
      setItemProduct,
      templateId,
    ]
  );

  const [copiedItem] = useDocItemsClipboard();
  const [pasteItemFromClipboardMutation] =
    useTemplateQuoteEdit_PasteItemFromClipboardMutation({ client });
  const pasteItem = React.useCallback(
    async (docId: string, parentItemId: string) => {
      if (!copiedItem) throw new Error("No item copied");

      await pasteItemFromClipboardMutation({
        variables: {
          input: {
            sourceProjectId: copiedItem.projectId,
            sourceDocId: copiedItem.docId,
            sourceItemId: copiedItem.itemId,
            destProjectId: null,
            destDocId: docId,
            destItemId: parentItemId,
            expandedItemIds: expandedItemIdsRef.current,
          },
        },
      });
    },
    [copiedItem, pasteItemFromClipboardMutation, expandedItemIdsRef]
  );
  const isAllowedToPasteItem = React.useCallback(
    (parentItem: {
      type: ItemType;
      linkedQuoteTemplate?: object | null | undefined;
    }) => {
      if (!copiedItem?.itemType || !parentItem.type) return false;
      if (parentItem.linkedQuoteTemplate) return false;

      return isAllowedToPaste({
        documentItemTypes: QUOTE_TEMPLATE_ITEM_TYPES,
        copiedItemType: copiedItem.itemType,
        parentItemType: parentItem.type,
      });
    },
    [copiedItem?.itemType]
  );

  return {
    createItem,
    createItemSection,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    createItemWithLinkedTemplate,
    pasteItem,
    isAllowedToPasteItem,
  };
}
